import type { Translations } from "./pt";

export const es: Translations = {
  language: {
    label: "Idioma",
    pt: "Português",
    en: "English",
    es: "Español",
  },
  theme: {
    label: "Tema",
    light: "Claro",
    dark: "Oscuro",
  },
  nav: {
    home: "Inicio",
    tickets: "Tickets",
    newTicket: "Nuevo Ticket",
    service: "Atención",
    sla: "SLA",
    faq: "FAQ",
    policy: "Política",
    catalog: "Catálogo",
    docs: "Documentación",
    admin: "Admin",
    reports: "Informes",
    queues: "Colas",
    changes: "Cambios",
    approvals: "Aprobaciones",
    problems: "Problemas",
    menu: "Menú",
    menuTitle: "Navegación",
    groupService: "Atención",
    groupManagement: "Gestión",
    groupKnowledge: "Conocimiento",
  },
  common: {
    login: "Iniciar sesión",
    logout: "Cerrar sesión",
    logoutSuccess: "Sesión cerrada",
    logoutDescription: "Has cerrado sesión correctamente.",
  },
  roles: {
    admin: "Admin Interno",
    internal: "Administrador",
    analyst: "Analista",
    external: "Usuario",
  },
  user: {
    myTickets: "Mis Tickets",
    servicePanel: "Panel de Atención",
    adminDashboard: "Panel Admin",
    serviceCatalog: "Catálogo de Servicios",
    editProfile: "Editar perfil",
  },
  profile: {
    title: "Editar perfil",
    name: "Nombre",
    email: "Correo electrónico",
    emailReadOnly: "El correo no se puede cambiar.",
    avatar: "Foto de perfil",
    avatarChange: "Cambiar foto",
    avatarUpload: "Subir foto",
    avatarCrop: "Recortar foto",
    avatarCropHint: "Ajusta el área de la foto. Arrastra para reposicionar y usa el zoom.",
    avatarCropConfirm: "Usar esta foto",
    avatarCropCancel: "Cancelar",
    changePassword: "Cambiar contraseña",
    sendCode: "Enviar código por correo",
    codeSent: "Código enviado. Revisa tu correo (válido 15 min).",
    code: "Código",
    newPassword: "Nueva contraseña",
    confirmPassword: "Confirmar nueva contraseña",
    save: "Guardar",
    saveName: "Guardar nombre",
    changePasswordSubmit: "Cambiar contraseña",
    successName: "Nombre actualizado.",
    successAvatar: "Foto actualizada.",
    successPassword: "Contraseña cambiada correctamente.",
    errorCode: "Código inválido o expirado.",
    errorPasswordMatch: "Las contraseñas no coinciden.",
    errorPasswordLength: "La contraseña debe tener al menos 6 caracteres.",
  },
  notifications: {
    title: "Notificaciones",
    markAllAsRead: "Marcar todas como leídas",
    none: "Ninguna notificación",
    markAllAsReadToast: "Notificaciones marcadas como leídas",
    markAllAsReadDesc: "Todas las notificaciones han sido marcadas como leídas.",
  },
  trial: {
    evaluation: "Período de evaluación",
    ended: "Evaluación finalizada",
    daysLeft: (n: number) =>
      `Tienes ${n} día${n === 1 ? "" : "s"} de prueba con tickets ilimitados.`,
    contact:
      "Tu período de evaluación ha terminado. Contáctanos para activar la facturación y seguir usando el sistema.",
    label: (n: number) =>
      `(Evaluación: ${n} día${n === 1 ? "" : "s"} restante${n === 1 ? "" : "s"})`,
    labelEnded: "(Evaluación finalizada)",
  },
  pageTitle: {
    default: "TEC-IT ITSM - Sistema de Gestión de Tickets",
  },
  businessHours: {
    badgeHuman: "Atención Humana",
    badgeOff: "Fuera de Horario",
    messageUntil: (time: string) => `Atención humana disponible hasta ${time}`,
    messageBot: (time: string) => `Atención por bot. Atención humana vuelve a las ${time}`,
  },
  dashboard: {
    loading: "Cargando tickets...",
    title: "Panel de Atención",
    subtitle: "Gestión completa de tickets - Vista Interna",
    refresh: "Actualizar",
    total: "Total",
    open: "Abiertos",
    inProgress: "En Atención",
    resolved: "Resueltos",
    slaExpired: "SLA Vencido",
    unassigned: "Sin Asignar",
    critical: "Crítica",
    queueN1: "Fila N1 - Primer Nivel",
    queueN2: "Fila N2 - Segundo Nivel",
    queueN3: "Fila N3 - Especializado",
    activeTickets: "Tickets activos",
    byCategory: "Tickets por Categoría",
    byTechnician: "Tickets por Técnico",
    noData: "Sin datos disponibles",
    qualityMetrics: "Métricas de Calidad",
    npsAverage: "NPS Medio",
    reopenRate: "Tasa de Reapertura",
    reopenedCount: (n: number) => `${n} tickets reabiertos`,
    transfersEscalations: "Transferencias y Escalamientos",
    totalTransfers: "Total de Transferencias",
    ticketsTransferred: "Tickets Transferidos",
    multipleTransfers: (n: number) => `${n} con múltiples transf.`,
    avgPerTicket: "Promedio por Ticket",
    filtersTitle: "Filtros y Búsqueda Avanzada",
    searchPlaceholder: "Buscar...",
    status: "Estado",
    statusActive: "En curso",
    statusAll: "Todos los Estados",
    statusOpen: "Abierto",
    statusInProgress: "En Atención",
    statusWaiting: "Esperando Respuesta",
    statusResolved: "Resuelto",
    statusClosed: "Cerrado",
    priority: "Prioridad",
    priorityAll: "Todas las Prioridades",
    priorityCritical: "Crítica",
    priorityHigh: "Alta",
    priorityMedium: "Media",
    priorityLow: "Baja",
    type: "Tipo",
    typeAll: "Todos los Tipos",
    typeIncident: "Incidente",
    typeRequest: "Solicitud",
    typeProblem: "Problema",
    typeChange: "Cambio",
    queue: "Fila",
    queueAll: "Todas las Filas",
    queueN1Short: "N1 - Primer Nivel",
    queueN2Short: "N2 - Segundo Nivel",
    queueN3Short: "N3 - Especializado",
    company: "Empresa",
    companyAll: "Todas las Empresas",
    assignee: "Responsable",
    assigneeAll: "Todos",
    assigneeUnassigned: "Sin Asignar",
    sla: "SLA",
    slaAll: "Todos",
    slaExpiredFilter: "SLA Vencido",
    slaWarning: "SLA Próximo",
    clearFilters: "Limpiar Filtros",
    ticketsList: "Tickets",
    ticketsListDesc: "Lista completa de tickets con información detallada",
    noCompany: "Sin empresa",
    noRequester: "Sin solicitante",
    category: "Categoría",
    created: "Creado",
    assigneeLabel: "Responsable",
    notAssigned: "No asignado",
    noTicketsFound: "Ningún ticket encontrado",
    adjustFilters: "Ajuste los filtros para encontrar tickets",
    linkedReported: (n: number) => (n === 1 ? "1 persona reportó lo mismo" : `${n} personas reportaron lo mismo`),
  },
  index: {
    serviceCenter: "Centro de Servicios",
    heroSubtitle: "Sistema completo de gestión de tickets técnicos y operativos.",
    heroSubtitle2: "Abra, siga y resuelva sus tickets con agilidad y transparencia.",
    accessSystem: "Acceder al Sistema",
    openTicket: "Abrir Ticket",
    ticketSystem: "Sistema de Tickets",
    ticketSystemDesc: "Sistema automatizado de categorización y priorización basado en ITIL",
    support24: "Soporte 24/7",
    support24Desc: "Comunicación en tiempo real con nuestro equipo técnico especializado",
    slaTransparent: "SLA Transparente",
    slaTransparentDesc: "Siga el progreso con indicadores visuales de tiempo de resolución",
    totalSecurity: "Seguridad Total",
    totalSecurityDesc: "Entorno seguro con control de acceso e historial completo",
    mainResources: "Recursos Principales",
    mainResourcesDesc: "Todo lo que necesita para gestionar sus tickets de forma eficiente",
    fullManagement: "Gestión Completa",
    fullManagementDesc: "Incidentes, Solicitudes, Problemas y Cambios en una única plataforma integrada",
    intuitiveDashboard: "Panel Intuitivo",
    intuitiveDashboardDesc: "Visualice KPIs, métricas de rendimiento y estado en tiempo real con gráficos interactivos",
    mobileFirst: "Mobile First",
    mobileFirstDesc: "Interfaz responsiva optimizada para todos los dispositivos, acceda desde cualquier lugar",
  },
  auth: {
    backToHome: "Volver al inicio",
    backToLogin: "Volver al login",
    accessAccount: "Acceda a su cuenta",
    loginTitle: "Iniciar sesión",
    loginDesc: "Ingrese sus credenciales para acceder al sistema de tickets",
    email: "Correo electrónico",
    emailPlaceholder: "su@email.com",
    password: "Contraseña",
    forgotPassword: "Olvidé mi contraseña",
    forgotPasswordAlt: "¿Olvidó la contraseña?",
    rememberMe: "Recordarme",
    centralDeServicos: "Centro de Servicios",
    accessSystem: "Acceder al Sistema",
    openTicket: "Abrir Ticket",
    registerCta: "Registrarse",
    hasAccount: "¿Ya tiene cuenta?",
    signInLink: "Iniciar sesión",
    tagline: "Sistema completo de gestión de tickets técnicos y operativos",
    signIn: "Entrar",
    signingIn: "Entrando...",
    noAccount: "¿No tiene cuenta?",
    createAccount: "Crear cuenta",
    welcomeTitle: "¡Bienvenido!",
    welcomeDesc: "Registre su empresa y comience a usar el sistema de tickets.",
    signUpCta: "Registrarse",
    detectingCompany: "Detectando empresa...",
    forcePasswordReset: "Cambio de contraseña obligatorio",
    setNewPassword: "Defina una nueva contraseña para continuar.",
    loginSuccess: "¡Inicio de sesión exitoso!",
    redirecting: "Redirigiendo al panel...",
    loginError: "Error en el login",
    invalidCredentials: "Credenciales inválidas",
    connectionError: "No se pudo conectar al servidor",
    checkEmail: "Revise su correo",
    checkEmailDesc: "Si el correo existe, recibirá el enlace.",
    recoveryError: "Error al solicitar recuperación",
    recoveryErrorDesc: "No se pudo procesar su solicitud.",
    recoverPassword: "Recuperar contraseña",
    forgotTitle: "Olvidé mi contraseña",
    forgotDesc: "Ingrese su correo para recibir el enlace de restablecimiento",
    sendLink: "Enviar enlace",
    sending: "Enviando...",
    resetPasswordTitle: "Restablecer contraseña",
    newPassword: "Nueva contraseña",
    newPasswordDesc: "Defina su nueva contraseña para acceder al sistema",
    invalidLink: "Enlace inválido o incompleto. Solicite un nuevo correo de recuperación.",
    confirmPassword: "Confirmar contraseña",
    savePassword: "Restablecer contraseña",
    saving: "Guardando...",
    passwordReset: "Contraseña restablecida",
    passwordResetDesc: "Ya puede iniciar sesión.",
    resetError: "Error al restablecer contraseña",
    resetErrorDesc: "No se pudo completar la operación.",
    invalidPassword: "Contraseña inválida",
    passwordMinLength: "La contraseña debe tener al menos 6 caracteres.",
    passwordsDontMatch: "Las contraseñas no coinciden",
    confirmPasswordCorrect: "Confirme la contraseña correctamente.",
  },
  register: {
    title: "Registro",
    desc: "Complete los datos para crear su cuenta",
    name: "Nombre completo",
    namePlaceholder: "Su nombre completo",
    phone: "Teléfono",
    phonePlaceholder: "(11) 98765-4321",
    companyDomain: "Dominio de la empresa",
    companyDomainPlaceholder: "ejemplo.com",
    companyDomainHint: "Ingrese el dominio de su empresa (sin www)",
    sector: "Sector",
    sectorPlaceholder: "Ej: TI, RRHH, Finanzas",
    function: "Función",
    functionPlaceholder: "Ej: Analista, Gerente, Coordinador",
    confirmPassword: "Confirmar contraseña",
    emailHint: "Use el correo corporativo de su empresa",
    passwordHint: "Mínimo 6 caracteres",
    creatingAccount: "Creando cuenta...",
    createButton: "Crear cuenta",
    error: "Error",
    nameRequired: "El nombre es obligatorio.",
    emailRequired: "El correo es obligatorio.",
    invalidEmail: "Correo inválido.",
    phoneRequired: "El teléfono es obligatorio.",
    domainRequired: "El dominio de la empresa es obligatorio.",
    sectorRequired: "El sector es obligatorio.",
    functionRequired: "La función es obligatoria.",
    passwordMin: "La contraseña debe tener al menos 6 caracteres.",
    passwordsDontMatch: "Las contraseñas no coinciden.",
    registerSuccess: "¡Cuenta creada con éxito!",
    registerSuccessDesc: "Redirigiendo al login...",
    registerSuccessMessage: "Tienes 20 días de evaluación con tickets ilimitados. Después, se requerirá facturación.",
    registerError: "Error al registrar",
  },
  landing: {
    loginSuccess: "¡Inicio de sesión exitoso!",
    redirecting: "Redirigiendo al panel...",
    loginError: "Error en el login",
    invalidCredentials: "Credenciales inválidas",
    connectionError: "No se pudo conectar al servidor",
    signIn: "Entrar",
    signingIn: "Entrando...",
    accessAccount: "Acceda a su cuenta",
    noAccount: "¿No tiene cuenta?",
    createAccount: "Crear cuenta",
    forgotPassword: "Olvidé mi contraseña",
    newAccount: "Crear cuenta",
  },
  itsmDashboard: {
    title: "Panel ITSM",
    subtitle: "Gestione y siga sus tickets",
    newTicket: "Nuevo Ticket",
    loading: "Cargando tickets...",
    error: "Error al cargar tickets",
    connectionError: "Error de conexión",
    totalTickets: "Total de Tickets",
    tryAgain: "Intentar de nuevo",
    searchPlaceholder: "Buscar por ID o título...",
    filtersTitle: "Filtros y Búsqueda",
    noTickets: "Ningún ticket encontrado",
    noTicketsDesc: "Aún no tiene tickets o los filtros no devolvieron resultados.",
    openTicket: "Abrir Ticket",
  },
  footer: {
    companyDesc: "Sistema profesional de gestión de tickets basado en las mejores prácticas ITIL.",
    quickAccess: "Acceso Rápido",
    myTickets: "Mis Tickets",
    openTicket: "Abrir Ticket",
    slaLevels: "Niveles de SLA",
    support: "Soporte",
    servicePolicy: "Política de Servicio",
    contact: "Contacto",
    available24: "24/7 Disponible",
    rightsReserved: "Todos los derechos reservados.",
  },
  notFound: {
    title: "404",
    message: "Página no encontrada",
    backHome: "Volver al inicio",
  },
  faq: {
    title: "Centro de Ayuda",
    subtitle: "Encuentre respuestas rápidas a las dudas más comunes sobre el sistema ITSM",
    searchPlaceholder: "Buscar en la FAQ...",
    noResults: "Ningún resultado encontrado",
    categoryGeneral: "General",
    categorySla: "SLA y Prioridades",
    categoryTypes: "Tipos de Ticket",
    categoryComm: "Comunicación",
    categoryTech: "Problemas Técnicos",
  },
  sla: {
    title: "Niveles de SLA",
    subtitle: "Nuestros compromisos de tiempo de respuesta y resolución basados en la clasificación ITIL. Los plazos comienzan desde la apertura del ticket en horario comercial (8h a 18h, lunes a viernes).",
    howItWorks: "Cómo funciona nuestro SLA",
    responseTime: "Tiempo de Respuesta",
    responseTimeDesc: "Tiempo máximo para que nuestro equipo haga el primer contacto e inicie la atención de su ticket.",
    resolutionTime: "Tiempo de Resolución",
    resolutionTimeDesc: "Tiempo máximo para la resolución completa del ticket, incluyendo pruebas y confirmación con el usuario.",
    serviceHours: "Horario de Atención",
    weekdays: "Lunes a Viernes: 08:00 a 18:00",
    emergencies: "Emergencias: 24/7 para tickets críticos",
    slaCalculation: "Cálculo del SLA",
    slaCalculationDesc: "Los plazos se calculan solo en horario comercial, excepto para tickets críticos.",
    response: "Respuesta",
    resolution: "Resolución",
    priority: "Prioridad",
    description: "Descripción",
    typeIncident: "Incidente",
    typeRequest: "Requisición",
    typeProblem: "Problema",
    typeChange: "Cambio",
    typeIncidentDesc: "Interrupción no planificada o reducción de la calidad de un servicio",
    typeRequestDesc: "Solicitud de un usuario de información, asesoramiento, cambio estándar o acceso",
    typeProblemDesc: "Causa desconocida de uno o más incidentes existentes o potenciales",
    typeChangeDesc: "Adición, modificación o eliminación de algo que pueda afectar los servicios",
    incidentCriticalDesc: "Sistema principal no disponible",
    incidentHighDesc: "Funcionalidad importante afectada",
    incidentMediumDesc: "Problema que afecta a pocos usuarios",
    incidentLowDesc: "Problema menor o cosmético",
    requestCriticalDesc: "Acceso urgente a sistema crítico",
    requestHighDesc: "Nueva funcionalidad importante",
    requestMediumDesc: "Solicitud estándar",
    requestLowDesc: "Información o mejoras",
    problemCriticalDesc: "Causa de múltiples incidentes críticos",
    problemHighDesc: "Patrón de incidentes recurrentes",
    problemMediumDesc: "Análisis preventivo",
    problemLowDesc: "Mejora de proceso",
    changeCriticalDesc: "Cambio de emergencia",
    changeHighDesc: "Cambio importante planificado",
    changeMediumDesc: "Cambio estándar",
    changeLowDesc: "Mejora no urgente",
    notesTitle: "Notas Importantes",
    criticalNote: "Tickets Críticos: Reciben atención 24/7, incluyendo fines de semana y festivos.",
    communicationNote: "Comunicación: Será notificado por correo en cada cambio de estado del ticket.",
    escalationNote: "Escalación: Los tickets que exceden el 80% del SLA se escalan automáticamente a supervisión.",
    slaPauseNote: "Pausa de SLA: El cronómetro se pausa cuando esperamos respuesta del solicitante.",
    reopenNote: "Reapertura: Los tickets pueden reabrirse hasta 48h después del cierre sin impacto en el SLA.",
  },
  servicePolicy: {
    title: "Política de Servicio",
    subtitle: "Conozca nuestros estándares de atención, responsabilidades y compromisos con la calidad del soporte técnico.",
    qualityCommitment: "Compromiso con la Calidad",
    introParagraph: "TEC-IT está comprometida en brindar soporte técnico de excelencia, siguiendo las mejores prácticas ITIL (Information Technology Infrastructure Library) para garantizar eficiencia, transparencia y satisfacción de nuestros usuarios.",
    availability: "Disponibilidad",
    satisfaction: "Satisfacción",
    forCritical: "Para Críticos",
    scopeTitle: "Alcance del Servicio",
    scopeDesc: "Qué está incluido en nuestro soporte técnico",
    included: "Servicios Incluidos",
    excluded: "Servicios No Incluidos",
    includedItem1: "Soporte a infraestructura de TI",
    includedItem2: "Mantenimiento de hardware y software",
    includedItem3: "Configuración de sistemas y redes",
    includedItem4: "Gestión de usuarios y accesos",
    includedItem5: "Respaldo y recuperación de datos",
    includedItem6: "Seguridad de la información",
    includedItem7: "Consultoría técnica",
    includedItem8: "Monitoreo de sistemas",
    excludedItem1: "Desarrollo de software personalizado",
    excludedItem2: "Capacitación básica de usuarios",
    excludedItem3: "Soporte a equipos personales",
    excludedItem4: "Configuraciones no corporativas",
    excludedItem5: "Recuperación de datos por negligencia",
    excludedItem6: "Mantenimiento físico de equipos antiguos",
    excludedItem7: "Licenciamiento de software de terceros",
    excludedItem8: "Soporte a sistemas no homologados",
    serviceHoursTitle: "Horarios de Atención",
    businessHours: "Horario Comercial",
    weekdays: "Lunes a Viernes:",
    weekdaysTime: "08:00 a 18:00",
    saturday: "Sábado:",
    saturdayTime: "08:00 a 12:00",
    sundayHolidays: "Domingo y Feriados:",
    sundayHolidaysTime: "Solo emergencias",
    support247Title: "Atención 24/7",
    criticalTicketsBadge: "Tickets Críticos",
    criticalTicketsDesc: "Para incidentes que afectan sistemas críticos o causan interrupción total de los servicios, ofrecemos soporte 24 horas al día, 7 días a la semana, incluidos feriados.",
    responsibilitiesTecit: "Responsabilidades de TEC-IT",
    responsibilitiesUser: "Responsabilidades del Usuario",
    respTecit1: "Responder a los tickets dentro del SLA establecido",
    respTecit2: "Mantener comunicación clara y transparente",
    respTecit3: "Documentar todas las acciones realizadas",
    respTecit4: "Escalar tickets cuando sea necesario",
    respTecit5: "Mantener confidencialidad de la información",
    respTecit6: "Proporcionar informes de desempeño",
    respUser1: "Proporcionar información completa y precisa",
    respUser2: "Responder a solicitudes de prueba/validación",
    respUser3: "Esperar aprobación antes de cambios",
    respUser4: "Mantener respaldo de datos importantes",
    respUser5: "Comunicar cambios en el entorno",
    respUser6: "Evaluar la atención después de la resolución",
    escalationTitle: "Proceso de Escalación",
    escalationDesc: "Cómo se escalan los tickets cuando es necesario",
    escalationAuto: "Escalación Automática",
    escalationAutoDesc: "Cuando el SLA alcanza el 80% del plazo",
    escalationTech: "Escalación Técnica",
    escalationTechDesc: "A especialistas cuando sea necesario",
    escalationMgmt: "Escalación Gerencial",
    escalationMgmtDesc: "Para supervisión en casos críticos",
    performanceIndicators: "Indicadores de Desempeño",
    mainKpis: "KPIs Principales",
    slaCompliance: "Cumplimiento de SLA",
    firstResolution: "Primera Resolución",
    userSatisfaction: "Satisfacción del Usuario",
    avgResponseTime: "Tiempo Medio de Respuesta",
    reportsTitle: "Informes",
    reportItem1: "Informe mensual de desempeño",
    reportItem2: "Dashboard en tiempo real",
    reportItem3: "Análisis de tendencias",
    reportItem4: "Retroalimentación de usuarios",
    reportItem5: "Planes de mejora continua",
    contactFeedbackTitle: "Contacto y Retroalimentación",
    contactFeedbackDesc: "Su opinión es fundamental para mejorar nuestros servicios",
    contactChannels: "Canales de Contacto",
    emailLabel: "Correo:",
    phoneLabel: "Teléfono:",
    chatLabel: "Chat:",
    systemLabel: "Sistema:",
    chatAvailable: "Disponible en el sistema",
    systemPortal: "Portal de tickets ITSM",
    complaintsSuggestions: "Quejas y Sugerencias",
    complaintsParagraph: "Para quejas, sugerencias o elogios, contacte a nuestro gestor de calidad en qualidade@tec-it.com",
    responseTimeNote: "Todos los mensajes son analizados y respondidos en un plazo de 48 horas hábiles.",
  },
  documentation: {
    title: "Documentación",
    subtitle: "Registro y estructura de base de datos.",
    loading: "Cargando...",
    loadError: "No se pudo cargar la documentación.",
  },
  floatingHelp: {
    help: "Ayuda",
    needHelp: "¿Necesita ayuda?",
    title: "Centro de Ayuda",
    description: "Encuentre respuestas rápidas y contacte nuestro soporte",
    ariaLabel: "Ayuda y Soporte",
    humanAvailable: "Atención Humana Disponible",
    outsideHours: "Fuera del Horario de Atención",
    quickAccess: "Acceso Rápido",
    contactUs: "Contáctenos",
    email: "Correo",
    phone: "Teléfono",
    adminPanel: "Panel Admin",
    adminPanelDesc: "Gestionar sistema y usuarios",
    adminPanelDescInternal: "Gestionar empresa y usuarios",
    service: "Atención",
    serviceDesc: "Gestionar tickets",
    reports: "Informes",
    reportsDesc: "Análisis y métricas",
    manageUsers: "Gestionar Usuarios",
    manageUsersDesc: "Administrar usuarios del sistema",
    manageUsersDescInternal: "Administrar usuarios de la empresa",
    faq: "FAQ",
    faqDesc: "Preguntas frecuentes",
    howToOpenTicket: "Cómo abrir un ticket",
    howToOpenTicketDesc: "Guía paso a paso",
    servicePolicy: "Política de Servicio",
    servicePolicyDesc: "Conozca nuestros SLAs",
    myTickets: "Mis Tickets",
    myTicketsDesc: "Seguimiento de sus tickets",
    systemSupport: "Soporte del Sistema",
    systemSupportDesc: "Abra un ticket directo con el equipo de la plataforma.",
    titleLabel: "Título *",
    titlePlaceholder: "Ej: Error al crear ticket...",
    descriptionLabel: "Descripción *",
    descriptionPlaceholder: "Describa el problema y pasos para reproducir.",
    impactLabel: "Impacto *",
    urgencyLabel: "Urgencia *",
    impactCritical: "Crítico",
    impactHigh: "Alto",
    impactMedium: "Medio",
    impactLow: "Bajo",
    sending: "Enviando...",
    sendTicketToSupport: "Enviar Ticket al Soporte",
    mySystemTickets: "Mis tickets del sistema",
    mySystemTicketsDesc: "Seguimiento de los últimos tickets que abrió para soporte global.",
    loadingTickets: "Cargando tickets...",
    noTicketsYet: "Aún no hay tickets abiertos.",
    requiredFields: "Campos obligatorios",
    requiredFieldsDesc: "Indique título y descripción.",
    ticketSent: "Ticket enviado",
    ticketSentDesc: (id: string) => `Ticket ${id} creado correctamente.`,
    error: "Error",
    errorSendDesc: "No se pudo abrir el ticket.",
    chatWithAdmin: "Chat con Administrador",
    createNewTicket: "Crear Nuevo Ticket",
    goToService: "Ir a Atención",
    needMoreHelp: "¿Necesita más ayuda? Nuestro equipo está listo para ayudarle.",
  },
  newTicket: {
    title: "Nuevo Ticket",
    subtitle: "Complete la información para abrir un nuevo ticket",
    backToTickets: "Volver a los tickets",
    restrictedAccess: "Acceso restringido",
    restrictedDesc: "Debe iniciar sesión para crear un ticket.",
    preselectedService: "Servicio preseleccionado",
    preselectedDesc: (name: string) => `El formulario fue prellenado con la información del servicio "${name}".`,
    requiredFields: "Campos obligatorios",
    requiredFieldsDesc: "Por favor complete: Qué necesita, Resumen, Descripción y Área del problema.",
    requiredImpactUrgency: "Por favor seleccione Impacto y Urgencia.",
    emailRequired: "Correo obligatorio",
    emailRequiredDesc: "Por favor ingrese el correo de la persona para quien es el ticket.",
    targetCompany: "Empresa de destino",
    targetCompanyHint: "El ticket se abrirá dentro de la base de esta empresa.",
    selectCompany: "Seleccione la empresa",
    loadingCompanies: "Cargando empresas...",
    noCompanies: "Ninguna empresa disponible",
    companyRequired: "Empresa obligatoria",
    companyRequiredDesc: "Seleccione en qué empresa se abrirá el ticket.",
    invalidEmail: "Correo inválido",
    invalidEmailDesc: "Por favor ingrese un correo válido.",
    outsideHours: "Fuera del Horario de Atención",
    outsideHoursDesc: (time: string) => `Está fuera del horario de atención (8h a 2h). Su ticket será creado y atendido cuando vuelva el horario. La atención vuelve a las ${time}.`,
    ticketInfo: "Información del Ticket",
    ticketInfoDesc: "Proporcione el máximo de detalles para agilizar la atención",
    whatYouNeed: "¿Qué necesita? *",
    select: "Seleccione...",
    resolveProblem: "Resolver un problema",
    requestSomething: "Solicitar algo nuevo",
    reportRecurring: "Reportar un problema recurrente",
    makeChange: "Hacer un cambio",
    summary: "Resumen del problema *",
    summaryPlaceholder: "Ej: No puedo abrir Excel",
    summaryHint: "Una frase corta describiendo el problema",
    tellMore: "Cuéntenos más sobre el problema *",
    tellMorePlaceholder: "Describa qué pasó, cuándo empezó y qué estaba haciendo cuando apareció el problema. Si hay mensaje de error, pégalo aquí.",
    tellMoreHint: "Cuantos más detalles, más rápido podremos ayudarle",
    areaOfProblem: "Área del problema *",
    selectArea: "Seleccione el área...",
    areaHint: "¿En qué área está el problema? Software, Hardware, Acceso, etc.",
    subcategoryOptional: "Tipo específico (opcional)",
    subcategoryPlaceholder: "Si no sabe, deje en blanco",
    impact: "Impacto *",
    urgency: "Urgencia *",
    selectImpact: "Seleccione el impacto...",
    selectUrgency: "Seleccione la urgencia...",
    critical: "Crítico",
    high: "Alto",
    medium: "Medio",
    low: "Bajo",
    additionalInfo: "Información Adicional",
    additionalInfoHint: "Complete lo que sepa. Si no sabe, puede dejarlo en blanco.",
    optional: "(opcional)",
    forWho: "Este ticket es para *",
    forMyself: "Para mí mismo",
    forOther: "Para otra persona",
    contactEmail: "Correo de Contacto *",
    yourEmailUsed: "(se usará su correo)",
    updatesWillBeSent: "Las actualizaciones del ticket se enviarán a:",
    contactPhone: "Teléfono de Contacto (opcional)",
    attachments: "Adjuntos",
    clickToUpload: "Haga clic para enviar o arrastre archivos",
    attachmentsHint: "PNG, JPG, PDF (MÁX. 10MB) • Pegue capturas con Ctrl+V",
    attachedFiles: "Archivos adjuntos:",
    remove: "Quitar",
    calculatedPriority: "Prioridad Calculada",
    basedOnImpact: (impact: string, urgency: string) => `Basada en impacto (${impact}) y urgencia (${urgency})`,
    estimatedSLA: "SLA Estimado",
    firstResponseTime: "Tiempo para primera respuesta",
    tipsTitle: "Consejos para un Ticket Eficiente",
    tip1: "Sea específico en título y descripción",
    tip2: "Incluya capturas o archivos relevantes",
    tip3: "Indique cuándo empezó el problema",
    tip4: "Describa los mensajes de error",
    createTicket: "Abrir Ticket",
    creating: "Creando ticket...",
    loadingCategories: "Cargando categorías...",
    printAdded: "Captura agregada",
    ticketCreated: "¡Ticket creado con éxito!",
    ticketCreatedDesc: (id: string) => `Su ticket ${id} fue creado. Recibirá actualizaciones por correo.`,
    ticketLinked: "¡Ticket creado y vinculado automáticamente!",
    ticketLinkedDesc: (id: string) => `Su ticket ${id} fue vinculado a un ticket existente con el mismo problema. Recibirá actualizaciones por correo.`,
    similarTicketsTitle: "Encontramos tickets con el mismo problema",
    similarTicketsDesc: "Otras personas ya reportaron esto. ¿Es el mismo problema que está enfrentando?",
    createNewAnyway: "No, crear nuevo ticket",
    useExisting: "Usar ticket existente",
    similarYesSame: "Sí, es el mismo",
    viewMyTicket: "Ver mi ticket",
    printAddedCount: (n: number) => `${n} imagen(es) agregada(s) a los adjuntos.`,
    reportedBy: "Reportado por:",
    thanksForInfo: "¡Gracias por la información!",
    teamWillCheck: "El equipo verificará si su ticket está relacionado. Si es el mismo problema, lo vincularán automáticamente.",
    ticketCreatedVerify: "El ticket fue creado. El equipo verificará la vinculación.",
    ticketsLinked: "¡Tickets vinculados!",
    ticketsLinkedDesc: "Su ticket fue vinculado al ticket principal. Cuando se resuelva, el suyo también.",
    errorCreating: "Error al crear ticket",
    errorCreatingDesc: "No se pudo crear el ticket. Intente de nuevo.",
    limitReached: "Límite de tickets alcanzado",
    configError: "Error de configuración",
    configErrorDesc: "La URL de la API no está configurada correctamente. Contacte al soporte técnico.",
    connectionError: "No se pudo conectar al servidor. Verifique su conexión e intente de nuevo.",
  },
  ticketDetails: {
    loading: "Cargando ticket...",
    notFound: "Ticket no encontrado",
    backToTickets: "Volver a los tickets",
    createdAt: "Creado el",
    updatedAt: "Actualizado el",
    description: "Descripción",
    communication: "Comunicación",
    communicationDesc: "Historial de mensajes entre usted y el equipo técnico",
    noMessages: "Aún no hay mensajes",
    startConversation: "Inicie la conversación agregando un comentario abajo",
    addCommentPlaceholder: "Agregue un comentario o pregunta...",
    attachImage: "Adjuntar Imagen (opcional, máx. 10MB)",
    selectImage: "Seleccionar Imagen",
    sendComment: "Enviar comentario",
    error: "Error",
    notFoundDesc: "Ticket no encontrado.",
    loadError: "No se pudo cargar el ticket.",
    fileTooBig: "Archivo muy grande",
    fileTooBigDesc: "La imagen debe tener como máximo 10MB.",
    invalidFileType: "Tipo de archivo inválido",
    invalidFileTypeDesc: "Solo se permiten imágenes.",
    attention: "Atención",
    addCommentOrImage: "Por favor agregue un comentario o una imagen.",
    commentAdded: "Comentario agregado",
    commentAddedDesc: "Su mensaje fue enviado correctamente.",
    ratingSent: "¡Evaluación enviada!",
    ratingSentDesc: "Gracias por su evaluación. Nos ayuda a mejorar continuamente.",
    reopenReasonRequired: "Motivo obligatorio",
    reopenReasonDesc: "Por favor indique el motivo de la reapertura. Será revisado por el equipo.",
    ticketReopened: "Ticket reabierto",
    ticketReopenedDesc: "El ticket fue reabierto con éxito. El motivo será revisado por el equipo.",
    rateExperience: "¿Cómo fue su atención?",
    npsLabel: "Evalúe de 0 a 10",
    submitRating: "Enviar evaluación",
    reopenTicket: "Reabrir ticket",
    reopenReason: "Motivo de reapertura",
    submitReopen: "Confirmar reapertura",
    user: "Usuario",
    attachedImage: "Imagen adjunta",
    sla: "SLA",
    timeRemaining: "Tiempo Restante",
    deadline: "Plazo Final",
    details: "Detalles",
    type: "Tipo",
    priority: "Prioridad",
    category: "Categoría",
    contactTime: "Contacto / Horario",
    assignee: "Responsable",
    notAssigned: "No asignado",
    requester: "Solicitante",
    notInformed: "No informado",
    priorityReportedAtOpen: "Prioridad indicada al abrir:",
    rateService: "Evalúe la Atención",
    ratingSentTitle: "Evaluación Enviada",
    thanksForRating: "¡Gracias por su evaluación!",
    yourOpinionHelps: "Su opinión nos ayuda a mejorar continuamente",
    yourNpsRating: "Su Evaluación NPS",
    scaleZeroToTen: "Escala de 0 a 10",
    yourComment: "Su Comentario",
    howLikelyRecommend: "¿Qué tan probable es que recomiende nuestra atención? (0-10)",
    promoter: "Promotor",
    neutral: "Neutral",
    detractor: "Detractor",
    promoterDesc: "¡Está muy satisfecho!",
    neutralDesc: "Satisfecho, pero puede mejorar",
    detractorDesc: "No está satisfecho",
    wouldYouComment: "¿Le gustaría dejar un comentario? (opcional)",
    commentHint: "Sus comentarios son muy importantes para nuestra formación y control de calidad.",
    commentPlaceholder: "Cuéntenos más sobre su experiencia con la atención...",
    ratingHelps: "Esta evaluación nos ayuda a mejorar nuestros servicios continuamente",
    reopenSectionTitle: "Reabrir Ticket",
    reopenSectionDesc: "Si el problema no se resolvió adecuadamente, puede reabrir este ticket",
    reopenReasonLabel: "Motivo de reapertura",
    reopenReasonPlaceholder: "Describa en detalle por qué debe reabrirse el ticket. Este motivo será revisado por el equipo...",
    reopenReasonHint: "El motivo es obligatorio y será revisado por el equipo antes de la reapertura",
    reopenDeadlineHint: "El ticket será reabierto y el motivo será revisado por el equipo",
    reopening: "Reabriendo...",
    sendingComment: "Enviando...",
  },
  internalTicketDetails: {
    loading: "Cargando ticket...",
    notFound: "Ticket no encontrado",
    backToPanel: "Volver al panel",
    description: "Descripción",
    companyInfo: "Información de la Empresa",
    company: "Empresa",
    requester: "Solicitante",
    notInformed: "No informado",
    sla: "SLA",
    slaOverdue: "Vencido",
    timeRemaining: "Tiempo Restante",
    deadline: "Plazo Final",
    details: "Detalles",
    type: "Tipo",
    category: "Categoría",
    impact: "Impacto",
    urgency: "Urgencia",
    priority: "Prioridad",
    priorityWithSlaSuffix: " (SLA / Analista)",
    customerPriority: "El solicitante indicó prioridad:",
    contact: "Contacto del Solicitante",
    assignee: "Responsable",
    notAssigned: "No asignado",
    reassign: "Reasignar",
    assign: "Asignar",
    relatedTickets: "Tickets Relacionados",
    mainTicket: "Ticket Principal",
    unlink: "Desvincular",
    viewMain: "Ver ticket principal",
    peopleReportedSame: "Personas que reportaron el mismo problema",
    peopleReportedCount: (n: number) => (n === 1 ? "1 persona reportó este mismo problema" : `${n} personas reportaron este mismo problema`),
    noLinkedTickets: "Este ticket no tiene otros tickets vinculados",
    communication: "Comunicación",
    communicationDesc: "Historial de mensajes e interacciones",
    noMessages: "Aún no hay mensajes",
    addCommentPlaceholder: "Agregue un comentario...",
    attachImagePaste: "Adjuntar Imagen (opcional, máx. 10MB) • Pegue capturas con Ctrl+V",
    selectImage: "Seleccionar Imagen",
    attachedImagePreview: "Vista previa de la imagen adjunta",
    removeImage: "Quitar imagen",
    sending: "Enviando...",
    addComment: "Agregar Comentario",
    history: "Historial de Cambios",
    historyDesc: "Registro completo de todos los cambios del ticket",
    noChanges: "Aún no hay cambios registrados",
    reasonLabel: "Motivo:",
    changedByLabel: "Por:",
    typeIncident: "Incidente",
    typeRequest: "Solicitud",
    typeProblem: "Problema",
    typeChange: "Cambio",
    changeTypeCreated: "Ticket Creado",
    changeTypeStatusChange: "Cambio de Estado",
    changeTypePriorityChange: "Cambio de Prioridad",
    changeTypeCategoryChange: "Cambio de Categoría",
    changeTypeTrackingStatusChange: "Cambio de Seguimiento",
    changeTypeQueueChange: "Cambio de Fila",
    changeTypeAssignment: "Asignación",
    changeTypeTransfer: "Transferencia",
    changeTypeLink: "Vinculación",
    changeTypeUnlink: "Desvincular",
    changeTypeReopen: "Reapertura",
    changeTypeSystemClose: "Cerrado por el Sistema",
    changeTypeCommentAdded: "Comentario Agregado",
    changeTypeAttachmentAdded: "Adjunto Agregado",
    changeTypeNpsRated: "Evaluación (NPS)",
    changeTypeFieldUpdate: "Actualización de Campo",
    parentIdLabel: "Vínculo (Ticket Principal)",
    commentLabel: "Comentario",
    attachmentsLabel: "Adjuntos",
    npsScoreLabel: "NPS",
    trackingPaused: "Pausado",
    noLink: "Sin vínculo",
    attachmentCount: (n: number) => (n === 1 ? "1 adjunto" : `${n} adjuntos`),
    manageTicket: "Gestionar Ticket",
    manageDesc: "Cambiar estado, prioridad y asignar responsable",
    status: "Estado",
    priorityLabel: "Prioridad",
    trackingStatus: "Estado de Seguimiento",
    queue: "Fila de Atención",
    assigneeLabel: "Responsable",
    selectAssignee: "Seleccione un responsable",
    removeAssignment: "Quitar Asignación",
    saveChanges: "Guardar Cambios",
    saving: "Guardando...",
    quickActions: "Acciones Rápidas",
    quickActionsDesc: "Resolver, cerrar o reabrir ticket rápidamente",
    closureReason: "Motivo del cierre",
    closureReasonPlaceholder: "Explique por qué se cierra el ticket...",
    closureReasonHint: "Este texto aparece en la comunicación y en el historial del ticket.",
    resolveTicket: "Resolver Ticket",
    closeTicket: "Cerrar Ticket",
    reopenTicket: "Reabrir Ticket",
    reopenDeadlineExpired: "El plazo para reabrir este ticket ha expirado.",
    transferTicket: "Transferir Ticket",
    transferDesc: "Elija cómo desea transferir",
    transferOption: "Opción de Transferencia",
    transferToTechnician: "Transferir a otro técnico",
    transferQueueOnly: "Transferir solo la fila (mantener responsable)",
    newAssignee: "Nuevo Responsable",
    destinationQueue: "Fila de Destino",
    optionalQueue: "Fila de Destino (opcional)",
    optionalQueuePlaceholder: "Automática (fila preferida del técnico)",
    optionalQueueHint: "Si no elige, la fila se definirá por la fila preferida del técnico.",
    transferReason: "Motivo de la Transferencia",
    transferReasonPlaceholder: "Explique el motivo de la transferencia...",
    transferBtn: "Transferir Ticket",
    queueChangeHint: "Cambiar la fila mueve el ticket a otro nivel",
    reopenTitle: "Reabrir Ticket",
    reopenDesc: "Reabrir ticket si el problema no se resolvió correctamente",
    reopenDialogDesc: (id: string | number) => `Indique el motivo de la reapertura del ticket #${id}`,
    reopenReasonLabel: "Motivo de la Reapertura",
    reopenReasonPlaceholder: "Explique por qué debe reabrirse el ticket...",
    reopenDeadline: "Plazo para reapertura:",
    cancel: "Cancelar",
    confirm: "Confirmar",
    reassignTicket: "Reasignar Ticket",
    assignTicket: "Asignar Ticket",
    selectForTicket: "Seleccione el responsable del ticket",
    ticketTitle: "Título del Ticket",
    currentAssignee: "Responsable Actual",
    assignTo: "Asignar a",
    notAssigned: "Sin asignar",
    printAttached: "Captura adjunta",
    printAttachedDesc: "Imagen pegada y agregada al comentario.",
    error: "Error",
    loadError: "No se pudieron cargar los datos del ticket.",
    commentAdded: "Comentario agregado",
    ticketUpdated: "Ticket actualizado",
    noChangesToSave: "Sin cambios",
    noChangesToSaveDesc: "No hay cambios para guardar.",
    updateError: "No se pudo actualizar el ticket.",
    transferSuccess: "Ticket transferido",
    assignSuccess: "Ticket asignado",
    assignmentRemoved: "Asignación quitada",
    assignmentRemovedDesc: "El ticket ya no tiene responsable.",
    reopenSuccess: "Ticket reabierto",
    resolveSuccess: "Ticket resuelto",
    closeSuccess: "Ticket cerrado",
    unlinkSuccess: "Ticket desvinculado",
    slaExpired: "SLA Vencido",
    statusOpen: "Abierto",
    statusInProgress: "En Atención",
    statusWaiting: "Esperando Respuesta",
    statusResolved: "Resuelto",
    statusClosed: "Cerrado",
    priorityCritical: "Crítica",
    priorityHigh: "Alta",
    priorityMedium: "Media",
    priorityLow: "Baja",
    queueN1: "N1 - Primer Nivel",
    queueN2: "N2 - Segundo Nivel",
    queueN3: "N3 - Especializado",
    trackingMonitoring: "Monitoreando",
    trackingWaiting: "Esperando",
    trackingResolved: "Resuelto",
    trackingEscalated: "Escalado",
    loadingTicketInfo: "Cargando información del ticket...",
    categoryInfra: "Infraestructura",
    categoryHardware: "Hardware",
    categorySoftware: "Software",
    categoryAccess: "Acceso y Permisos",
    categoryPerformance: "Rendimiento",
    roleAdmin: "Admin",
    roleAnalyst: "Analista",
    roleAttendant: "Atendente",
    meShort: "Yo",
    noName: "Sin nombre",
    noEmail: "Sin email",
    actions: "Acciones",
    reasonRequired: "Motivo obligatorio",
    reasonRequiredDesc: "Indique el motivo del cierre para resolver el ticket.",
    selectTechnician: "Seleccione el técnico",
    selectQueue: "Seleccione la fila",
    noAssigneeTransfer: "Este ticket no tiene responsable. Seleccione un técnico para transferir.",
    noAssigneeQueue: "Este ticket no tiene responsable. Para cambiar la fila, primero asigne un técnico.",
    pleaseProvideReopen: "Por favor, indique el motivo de la reapertura.",
  },
  serviceCatalog: {
    title: "Catálogo de Servicios",
    subtitle: "Elija un servicio para abrir un ticket",
    subtitleAlt: "Conozca los servicios disponibles y sus plazos de atención",
    searchPlaceholder: "Buscar servicios...",
    openTicket: "Abrir Ticket",
    noServices: "Ningún servicio encontrado",
    noServicesDesc: "Intente ajustar los filtros o la búsqueda.",
    allCategories: "Todas las categorías",
    all: "Todos",
    addService: "Agregar Servicio",
    loading: "Cargando...",
    noServicesFilter: "Ningún servicio encontrado con los filtros seleccionados.",
    category: "Categoría:",
    sla: "SLA:",
    estimatedTime: "Tiempo Estimado:",
    requestService: "Solicitar Servicio",
    addServiceTitle: "Agregar Servicio al Catálogo",
    addServiceDesc: "Los administradores pueden agregar nuevos servicios para la empresa.",
    name: "Nombre *",
    namePlaceholder: "Ej: Solicitar Acceso al Sistema X",
    description: "Descripción *",
    descriptionPlaceholder: "Explique qué cubre este servicio...",
    categoryLabel: "Categoría *",
    categoryPlaceholder: "Ej: Infraestructura",
    type: "Tipo *",
    select: "Seleccione",
    incident: "Incidente",
    request: "Requisición",
    problem: "Problema",
    change: "Cambio",
    slaOptional: "SLA (opcional)",
    slaPlaceholder: "Ej: 24h - 48h",
    estimatedTimeOptional: "Tiempo Estimado (opcional)",
    estimatedTimePlaceholder: "Ej: 1-2 días",
    company: "Empresa *",
    selectCompany: "Seleccione la empresa",
    companyRequiredHint: "Para admin global (sin empresa), es obligatorio elegir.",
    cancel: "Cancelar",
    save: "Guardar",
    requiredFields: "Campos obligatorios",
    requiredFieldsDesc: "Complete Nombre, Descripción, Categoría y Tipo.",
    companyRequired: "Empresa obligatoria",
    companyRequiredDesc: "Seleccione la empresa para agregar el servicio.",
    serviceAdded: "Servicio agregado",
    serviceAddedDesc: "El ítem ya aparece en el Catálogo.",
    error: "Error",
    errorLoad: "No se pudo cargar el catálogo.",
    errorRemove: "Error al quitar el servicio.",
    errorAdd: "Error al agregar el servicio.",
    removeConfirm: "¿Desea quitar este servicio del catálogo?",
    removed: "Quitado",
    removedDesc: "Servicio quitado del catálogo.",
    deleteBtn: "Borrar",
    removeTitle: "Quitar del catálogo",
    selectPlaceholder: "Seleccione...",
    default1Name: "Soporte a Infraestructura",
    default1Desc: "Atención para problemas de servidores, red, correo y conectividad",
    default1Category: "Infraestructura",
    default2Name: "Soporte a Hardware",
    default2Desc: "Reparación y mantenimiento de equipos, portátiles, impresoras y periféricos",
    default2Category: "Hardware",
    default3Name: "Soporte a Software",
    default3Desc: "Instalación, configuración y resolución de problemas en aplicaciones",
    default3Category: "Software",
    default4Name: "Solicitud de Acceso",
    default4Desc: "Creación de usuarios, permisos y acceso a sistemas",
    default4Category: "Acceso y Permisos",
    default5Name: "Análisis de Rendimiento",
    default5Desc: "Investigación de problemas de lentitud y optimización de sistemas",
    default5Category: "Rendimiento",
    default6Name: "Cambios en Infraestructura",
    default6Desc: "Actualizaciones, migraciones y cambios en servidores y red",
    default6Category: "Infraestructura",
  },
  reports: {
    title: "Informes",
    subtitle: "Genere informes de tickets por período",
    subtitleAlt: "Genere informes detallados sobre el desempeño de los tickets",
    generateReport: "Generar Informe",
    generating: "Generando...",
    selectDates: "Seleccione fechas inicial y final",
    reportGenerated: "Informe generado",
    reportGeneratedDesc: "El informe fue generado correctamente.",
    error: "Error",
    errorGenerating: "No se pudo generar el informe.",
    generateFirst: "Genere un informe primero",
    exportPdf: "Exportar PDF",
    exportCsv: "Exportar CSV",
    exportExcel: "Exportar Excel",
    period: "Período",
    startDate: "Fecha inicial",
    endDate: "Fecha final",
    reportType: "Tipo de Informe",
    summary: "Resumen",
    configureReport: "Configurar Informe",
    configureDesc: "Seleccione el tipo de informe y el período deseado",
    summaryExecutive: "Resumen Ejecutivo",
    detailed: "Detallado",
    slaAnalysis: "Análisis de SLA",
    byTechnician: "Por Técnico",
    byCategory: "Por Categoría",
    exportSuccess: "Exportado con éxito",
    exportCsvDesc: "Archivo CSV descargado",
    exportExcelDesc: "Archivo Excel descargado",
    allowPopups: "Permita ventanas emergentes para exportar PDF",
    openingPrint: "Abriendo impresión",
    openingPrintDesc: "Use la opción 'Guardar como PDF' en la ventana de impresión",
    reportMonthlyPdf: "Informe Mensual (PDF)",
    performanceCsv: "Datos de Rendimiento (CSV)",
    satisfactionExcel: "Satisfacción del Usuario (Excel)",
    exportReports: "Exportar Informes",
    exportReportsDesc: "Generar informes detallados para análisis",
    ticketsByCategory: "Tickets por Categoría",
    slaMetrics: "Métricas de SLA",
    slaCompliance: "SLA Cumplido",
    averageResolutionTime: "Tiempo Medio de Resolución",
    averageResponseTime: "Tiempo Medio de Respuesta",
    firstResolution: "Primera Resolución",
    reportTitle: "Informe de Tickets",
    periodLabel: "Período",
    metric: "Métrica",
    value: "Valor",
    totalTickets: "Total de Tickets",
    backlog: "Backlog",
    backlogOverdue: "Backlog en Atraso",
    backlogSlaBreached: "Backlog Incumplió SLA",
    backlogTransferred: "Backlog Transferido",
    totalTransferred: "Total Transferidos",
    multipleTransfers: "Múltiples Transferencias",
    open: "Abiertos",
    resolved: "Resueltos",
    closed: "Cerrados",
    reopenRate: "Tasa de Reapertura",
    npsAverage: "NPS Medio",
    category: "Categoría",
    quantity: "Cantidad",
    ticketsByTechnician: "Tickets por Técnico",
    technician: "Técnico",
    noDataInPeriod: "Sin datos en el período",
    slaComplianceShort: "Cumplimiento SLA",
    violations: "Violaciones",
    backlogByAge: "Backlog por Antigüedad",
    age: "Antigüedad",
    metOfTotal: (met: number, total: number) => `${met} de ${total} cumplidos`,
    overdueTickets: "Tickets en Atraso",
    daysOverdue: (n: number) => `${n} días en atraso`,
    performanceByTechnician: "Rendimiento por Técnico",
    ticketsCount: (n: number) => `${n} tickets`,
    averageTime: "Tiempo Medio",
    categoryAnalysis: "Análisis por Categoría",
    percentOfTotal: (p: number) => `${p}% del total`,
    noTechnicianFound: "Ningún técnico encontrado en el período",
    noCategoryFound: "Ninguna categoría encontrada en el período",
    periodSummary: "Resumen del Período",
    backlogSlaViolations: "Backlog - Violaciones SLA",
    overdueOpen: "En atraso (abiertos)",
    passedSla: "Incumplieron SLA (total)",
    violationsInPeriod: "Violaciones en el período",
    transferredTicketsCard: "Tickets Transferidos",
    totalTransferredShort: "Total transferidos",
    backlogTransferredShort: "Backlog transferido",
    multipleTransfersShort: "Múltiples transferencias",
    transferredAnalysis: "Análisis de Tickets Transferidos",
    transferredStatsDesc: "Estadísticas sobre tickets transferidos entre técnicos",
    distribution: "Distribución",
    transferredAndResolved: "Transferidos y resueltos",
    transferredStillOpen: "Transferidos aún abiertos",
    multipleTransfersRate: "Tasa de múltiples transferencias",
    ofTotal: (p: number) => `${p}% del total`,
    ofBacklog: (p: number) => `${p}% del backlog`,
    transferredMoreThanOnce: "Tickets transferidos más de una vez",
    slaQuality: "Cumplimiento SLA",
    violationsInPeriodShort: (n: number) => `${n} violaciones en el período`,
    slaByCategory: "SLA por Categoría",
    metOfTotalDetail: (met: number, withSla: number, total: number) => `${met} de ${withSla} cumplidos (${total} total)`,
    generatedAt: "Generado el",
    inOverdue: "en atraso",
    slaBreachedShort: "incumplieron SLA",
    transferredShort: "transferidos",
  },
  adminDashboard: {
    title: "Panel Admin",
    subtitle: "Gestión de tickets, empresas y usuarios",
    subtitleAlt: "Visión general del equipo técnico y rendimiento del ITSM",
    tickets: "Tickets",
    companies: "Empresas",
    users: "Usuarios",
    loading: "Cargando...",
    loadingData: "Cargando datos...",
    totalTickets: "Total Tickets",
    queueTab: "Fila de Atención",
    technicians: "Técnicos",
    settings: "Configuración",
    queueTitle: "Fila de Tickets",
    queueDesc: "Gestionar asignaciones y prioridades",
    averageTime: "Tiempo Medio",
    satisfaction: "Satisfacción",
    total: "Total",
    results: "Resultados",
    active: "Activa",
    inactive: "Inactiva",
    activePlural: "Activas",
    inactivePlural: "Inactivas",
    activeMale: "Activos",
    inactiveMale: "Inactivos",
    edit: "Editar",
    deactivate: "Desactivar",
    remove: "Quitar",
    filterByStatus: "Filtrar por estado",
    filterByProfile: "Filtrar por perfil",
    filterByCompany: "Filtrar por empresa",
    all: "Todas",
    allProfiles: "Todos los Perfiles",
    allStatuses: "Todos los Estados",
    allCompanies: "Todas las Empresas",
    noCompany: "Sin Empresa",
    companyManagement: "Gestión de Empresas",
    companyManagementDesc: "Registre y gestione empresas usuarias del sistema",
    searchCompanyPlaceholder: "Buscar por nombre, dominio o teléfono...",
    newCompany: "Nueva Empresa",
    downloadTemplate: "Descargar plantilla",
    importCsv: "Importar CSV",
    ticketsUnlimited: "Ilimitado",
    ticketsCount: (used: number, limit: number) => `Tickets: ${used}/${limit}`,
    peopleCount: (n: number) => (n === 1 ? "1 persona registrada" : `${n} personas registradas`),
    userManagement: "Gestión de Usuarios",
    userManagementDesc: "Visualice, filtre y gestione todos los usuarios del sistema",
    importUsers: "Importar Usuarios",
    addUser: "Agregar Usuario",
    searchUserPlaceholder: "Buscar por nombre o correo...",
    company: "Empresa",
    domain: "Dominio",
    resetPassword: "Restablecer Contraseña",
    delete: "Eliminar",
    manageCategories: "Gestionar Categorías",
    manageCategoriesDesc: "Agregar o editar categorías de tickets",
    ticketsCountLabel: (n: number) => (n === 1 ? "1 ticket" : `${n} tickets`),
    categoryTicketsCount: (n: number) => (n === 1 ? "(1 ticket)" : `(${n} tickets)`),
    noCategoriesFound: "No se encontraron categorías. Agregue una nueva categoría para comenzar.",
    addNewCategory: "Agregar Nueva Categoría",
    addCategoryDialogTitle: "Agregar Nueva Categoría",
    addCategoryDialogDesc: "Cree una nueva categoría de tickets. La categoría estará disponible al crear nuevos tickets.",
    categoryNameLabel: "Nombre de la Categoría",
    categoryNamePlaceholder: "Ej: Seguridad TI",
    categoryNameHint: "Ingrese el nombre de la categoría que desea agregar",
    add: "Agregar",
    editCategoryDialogTitle: "Editar Categoría",
    editCategoryDialogDesc: (name: string) => `Renombrar la categoría "${name}". Todos los tickets con esta categoría serán actualizados.`,
    newCategoryNameLabel: "Nuevo Nombre de la Categoría",
    newCategoryNamePlaceholder: "Ingrese el nuevo nombre",
    removeCategoryDialogTitle: "Quitar Categoría",
    removeCategory: "Quitar Categoría",
    exportNps: "Exportar Votaciones NPS",
    exportNpsDesc: "Exporte todas las evaluaciones NPS con comentarios para formación y control de calidad",
    training: "Formación",
    qualityControl: "Control de Calidad",
    xlsxRecommended: "XLSX recomendado",
    exportIncludes: "La exportación incluye: puntuación NPS (0-10), comentarios, datos del ticket, cliente, técnico responsable, empresa y fechas relevantes.",
    format: "Formato",
    selectFormat: "Seleccione el formato",
    xlsxRecommendedOption: "XLSX (recomendado)",
    exporting: "Exportando...",
    exportXlsx: "Exportar XLSX",
    exportXls: "Exportar XLS",
    exportCsv: "Exportar CSV",
    xlsxKeepsFields: "El XLSX mantiene cada campo en su propia celda.",
    newCompanyDialogTitle: "Nueva Empresa",
    editCompanyDialogTitle: "Editar Empresa",
    editUserDialogTitle: "Editar Usuario",
    addUserDialogTitle: "Agregar Usuario",
    cancel: "Cancelar",
    saveChanges: "Guardar Cambios",
    activate: "Activar",
    noUsersFound: "No se encontraron usuarios con los filtros aplicados",
    noCompaniesFound: "Ninguna empresa encontrada",
    createFirstCompany: "Crear Primera Empresa",
    importCompanies: "Importar Empresas",
    newCompanyDialogDesc: "Registre una nueva empresa usuaria en el sistema",
    editCompanyDialogDesc: "Actualice la información de la empresa según sea necesario",
    editUserDialogDesc: "Actualice la información del usuario según sea necesario.",
    addUserDialogDesc: "Cree un nuevo analista y asígnelo a la empresa correcta.",
    importUsersDialogDesc: "Descargue la plantilla, complétela y envíe la hoja de cálculo. Campos normales: nombre, email, teléfono, dominio_empresa, sector, función.",
    importCompaniesDialogDesc: "Descargue la plantilla, complétela y envíe el CSV. Campos obligatorios: name y domain. El resto sigue el registro normal.",
    noFileChosen: "Ningún archivo elegido",
    importing: "Importando...",
    saving: "Guardando...",
    sendCsv: "Enviar CSV",
    internalUser: "Usuario interno",
    removeCategoryDialogDesc: (name: string) => `¿Está seguro de que desea quitar la categoría "${name}"?`,
    selectTargetCategoryPlaceholder: "Seleccione la categoría de destino",
    roleAdminInternal: "Admin Interno",
    roleAdministrator: "Administrador",
    roleAnalyst: "Analista",
    roleUser: "Usuario",
    downloadTemplateReady: "Descargar plantilla",
    chooseFile: "Elegir archivo",
    spreadsheet: "Hoja de cálculo",
    import: "Importar",
    nameLabel: "Nombre",
    emailLabel: "Correo",
    fullNamePlaceholder: "Nombre completo",
    filterByQueue: "Filtrar por fila",
    filterByTechnician: "Filtrar por técnico",
    filterByLevel: "Nivel de atención",
    allQueues: "Todas las Filas",
    allTechnicians: "Todos los Técnicos",
    allLevels: "Todos los Niveles",
    queueInfra: "Infraestructura",
    queueHardware: "Hardware",
    queueSoftware: "Software",
    queueSecurity: "Seguridad TI",
    levelN1: "N1 - Primer Nivel",
    levelN2: "N2 - Segundo Nivel",
    levelN3: "N3 - Especializado",
    slaExpiredBadge: "SLA VENCIDO",
    transferCount: (n: number) => `${n} transf.`,
    categoryLabel: "Categoría",
    requesterLabel: "Solicitante",
    slaLabel: "SLA",
    assigneeLabel: "Responsable",
    notAssigned: "Sin asignar",
    assign: "Asignar",
    reassign: "Reasignar",
    urgentAction: "Acción urgente necesaria",
    clickToOpen: "Clic para abrir el ticket",
    noTicketsQueue: "No se encontraron tickets. Los botones de asignación aparecerán aquí cuando haya tickets.",
    noTicketsFiltered: "No se encontraron tickets con los filtros aplicados. Ajuste los filtros arriba.",
    techniciansPerformance: "Rendimiento de Técnicos",
    techniciansPerformanceDesc: "Distribución de carga de trabajo y productividad",
    resolved: "resueltos",
    resolutionRate: "Tasa de resolución",
    ticketTitle: "Título del Ticket",
    currentAssignee: "Responsable Actual",
    assignTo: "Asignar a",
    loadingUsers: "Cargando usuarios...",
    selectAssignee: "Seleccione un responsable",
    me: "(Yo)",
    confirm: "Confirmar",
    assignTicketTitle: "Asignar Ticket",
    reassignTicketTitle: "Reasignar Ticket",
    assignTicketDialogDesc: (id: string | number) => `Seleccione el responsable del ticket ${id}`,
    // Configuración
    brandingTitle: "Identidad Visual de la Empresa",
    brandingDesc: (company: string) => `Personalice logo y colores del portal de ${company}.`,
    brandingYourCompany: "su empresa",
    loadingBranding: "Cargando branding...",
    lastSavedConfig: "Última configuración guardada",
    logoSaved: "Logo guardado",
    noLogoSaved: "Sin logo guardado.",
    primaryColor: "Primaria",
    secondaryColor: "Secundaria",
    companyLogo: "Logo de la Empresa",
    uploadLogoHint: "Envíe un archivo PNG/JPG (máximo recomendado 1MB).",
    primaryColorLabel: "Color Primario",
    secondaryColorLabel: "Color Secundario",
    preview: "Vista previa",
    saveBranding: "Guardar Identidad Visual",
    brandingUpdated: "Branding actualizado",
    brandingUpdatedDesc: "Los colores y el logo se han guardado correctamente.",
    logoUploaded: "Logo enviado",
    logoUploadedDesc: "Logo actualizado correctamente.",
    brandingSaveError: "No se pudo guardar el branding.",
    logoUploadError: "No se pudo enviar el logo.",
    errorTitle: "Error",
    categoryAdded: "Categoría agregada",
    categoryUpdated: "Categoría actualizada",
    categoryRemoved: "Categoría eliminada",
    categoryDisplayName: (name: string) => {
      const map: Record<string, string> = {
        "Acesso e Permissões": "Acceso y Permisos",
        "Infraestrutura": "Infraestructura",
        "Performace": "Rendimiento",
        "Performance": "Rendimiento",
        "Hardware": "Hardware",
        "Software": "Software",
      };
      return map[name] ?? name;
    },
  },
};
